import { useEffect, useState } from "react";
import {
    BooleanInput,
    Create,
    Datagrid,
    DeleteButton,
    Edit,
    Error,
    FunctionField,
    List,
    Loading,
    ReferenceInput,
    Resource,
    SelectInput,
    Show,
    SimpleForm,
    useCreate,
    useDataProvider,
    useDeleteMany,
    useGetList,
    useNotify,
    useRedirect,
    useRefresh
} from "react-admin";

import { useTokenOps } from "../components/TokenContext";
import { Filters } from "./BasicResource";

// Feature name mapping for better UI labels
const getFeatureDisplayName = (name) => {
    const nameMapping = {
        'Integration': 'API Integration (Side Lateral)'
    };
    return nameMapping[name] || name;
};

const AccountFeaturesList = (props) => {
    return (
        <List {...props} filters={Filters(props)}>
            <div style={{marginTop: '15px'}}>
                <Datagrid rowClick="show">
                    <FunctionField
                        label="Account Features"
                        render={record => record.feature ? getFeatureDisplayName(record.feature.name) : ''}
                    />
                    <DeleteButton/>
                </Datagrid>
            </div>
        </List>
    );
};

const ShowAccountFeatures = (props) => {
    return (
        <Show {...props}>
            <SimpleForm>
                <FunctionField
                    label="Account Features"
                    render={record => record.feature ? getFeatureDisplayName(record.feature.name) : ''}
                />
            </SimpleForm>
        </Show>
    );
};

const CreateAccountFeatures = (props) => {
    const refresh = useRefresh();
    const [create] = useCreate();
    const [deleteMany] = useDeleteMany();
    const dataProvider = useDataProvider();
    const tokenOps = useTokenOps();
    const [accountFeatures, setAccountFeatures] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const { data: features, loading: loadingFeatures, error: errorFeatures } = useGetList(
        'features',
        { page: 1, perPage: 100 },
        { field: 'id', order: 'ASC' }
    );

    const notify = useNotify();
    const redirect = useRedirect();

    const validateFeatureInAccountFeature = (featureId) => {
        return accountFeatures.some(({featureId: id}) => id === featureId);
    }

    const addFeatures = async (featuresToAdd) => {
        if (!featuresToAdd || featuresToAdd.length === 0) {
            return;
        }

        const accountId = tokenOps.account?.data?.accountId;

        if (!accountId) {
            notify('Error: No account selected', 'error');
            return;
        }

        const createPromises = featuresToAdd.map(id =>
            create('accountFeatures', {
                featureId: id,
                accountId: accountId
            })
        );

        await Promise.all(createPromises);
    }

    // Compute initial values for the form based on current account features
    const getInitialValues = () => {
        if (!features || !accountFeatures) return {};

        const initialValues = {};
        Object.values(features).forEach(({id}) => {
            initialValues[id] = validateFeatureInAccountFeature(id);
        });
        return initialValues;
    };

    const handleSubmit = async (values) => {
        try {
            const idFeaturesToAdd = [];
            const idFeaturesToRemove = [];
            Object.entries(values).map(value => value[1] ? idFeaturesToAdd.push(value[0]) : idFeaturesToRemove.push(value[0]))

            const featuresToAdd = idFeaturesToAdd?.filter(id => !accountFeatures.some(({featureId}) => id === featureId));

            const featuresToRemove = accountFeatures.filter(({featureId}) => idFeaturesToRemove?.some(id => featureId === id));
            if (featuresToRemove.length > 0) {
                await deleteMany('accountFeatures', featuresToRemove.map(({id}) => id))
            }
            await addFeatures(featuresToAdd)

            // Display a success notification
            notify('Record updated successfully');
            // Redirect the user to a different page after successful update
            redirect('/accountFeatures');
            refresh();
        } catch (error) {
            // Handle any errors that occur during the update process
            notify('Error: Unable to update record', 'error');
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchAccountFeatures = async () => {
            dataProvider.getList('accountFeatures', { pagination: { page: 1, perPage: 100 }, sort: { field: 'id', order: 'ASC' } })
                .then(({ data }) => {
                    if (isMounted) {
                        setAccountFeatures(data);
                        setLoading(false);
                    }
                })
                .catch(error => {
                    setError(error);
                    setLoading(false);
                });
        }

        fetchAccountFeatures();

        return () => {
            isMounted = false;
        }
    }, [dataProvider]);

    if (loadingFeatures || loading) return <Loading />;
    if (errorFeatures || error) return <Error />;
    if (!features) return null;

    return (
            <Create {...props}>
                <SimpleForm save={handleSubmit} initialValues={getInitialValues()}>
                    {
                        Object.values(features).map(({id, name}) => (
                            <BooleanInput
                                key={id}
                                source={id}
                                label={getFeatureDisplayName(name)}
                                className="account-feature-checkbox"
                            />
                        ))
                    }
                </SimpleForm>
            </Create>
    );
};

const EditAccountFeatures = (props) => {
    return (
        <Edit {...props}>
            <SimpleForm>
                <ReferenceInput label="Account Feature" source="featureId" reference="features">
                    <SelectInput source="name" disabled={true} />
                </ReferenceInput>
            </SimpleForm>
        </Edit>
    );
};

const accountFeaturesResource = () => <Resource
    name="accountFeatures"
    options={{label: 'Account features', menuGroup: 'Accounts'}}
    list={AccountFeaturesList}
    edit={EditAccountFeatures}
    create={CreateAccountFeatures}
    show={ShowAccountFeatures}/>;

export default accountFeaturesResource;
